import { Component, Input, Output, EventEmitter } from '@angular/core';
import { PAGE_SIZE } from '../../models/period-trends.const';

// Sentinel emitted between non-contiguous page numbers in `pages`. Rendered
// as a non-clickable "…" placeholder by the template.
export const PAGE_ELLIPSIS = '…';
export type PageItem = number | typeof PAGE_ELLIPSIS;

// Pagination strip used by every tab's tables. Removes 8x copies
// of the same prev/numbered/next button block from the markup.
@Component({
  selector: 'tangram-analytics-pagination',
  templateUrl: './analytics-pagination.component.html',
  styleUrls: ['./analytics-pagination.component.scss']
})
export class AnalyticsPaginationComponent {
  @Input() page = 1;
  @Input() total = 0;
  @Input() pageSize = PAGE_SIZE;
  @Output() pageChange = new EventEmitter<number>();

  // How many neighbours of the current page to keep in the window when the
  // current page is in the middle of the strip. With siblings = 1 the
  // middle layout reads as "1 … 14 15 16 … 31".
  private readonly SIBLINGS = 1;

  // Threshold below which we just render every page number — the strip is
  // already short enough that ellipses would be visual noise.
  private readonly COMPACT_LIMIT = 7;

  readonly ELLIPSIS: typeof PAGE_ELLIPSIS = PAGE_ELLIPSIS;

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  // Windowed page list with ellipses inserted between non-contiguous spans.
  // Always pins the first and last page so users can jump to either end in
  // one click; what's between depends on where `page` sits:
  //
  //   • near the start (page ≤ 4)        → "1 2 3 4 5 … N"
  //   • in the middle                     → "1 … P-1 P P+1 … N"
  //   • near the end (page ≥ N − 3)       → "1 … N-4 N-3 N-2 N-1 N"
  //
  // The strip is always at most 7 slots wide (5 numbers + 2 ellipses), which
  // matches the most common pagination convention and keeps the control
  // a fixed, predictable size regardless of how many pages the table has.
  get pages(): PageItem[] {
    const total = this.totalPages;
    if (total <= this.COMPACT_LIMIT) {
      return this.range(1, total);
    }

    const current = Math.min(Math.max(this.page, 1), total);
    const nearStart = current <= 3 + this.SIBLINGS;        // 1..4 with siblings=1
    const nearEnd = current >= total - (2 + this.SIBLINGS); // N-3..N with siblings=1

    if (nearStart) {
      // [1, 2, 3, 4, 5, …, N]
      return [...this.range(1, 4 + this.SIBLINGS), PAGE_ELLIPSIS, total];
    }

    if (nearEnd) {
      // [1, …, N-4, N-3, N-2, N-1, N]
      return [1, PAGE_ELLIPSIS, ...this.range(total - (3 + this.SIBLINGS), total)];
    }

    // [1, …, P-1, P, P+1, …, N]
    return [
      1,
      PAGE_ELLIPSIS,
      ...this.range(current - this.SIBLINGS, current + this.SIBLINGS),
      PAGE_ELLIPSIS,
      total,
    ];
  }

  get rangeStart(): number {
    return this.total === 0 ? 0 : (this.page - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.page * this.pageSize, this.total);
  }

  isNumber(p: PageItem): p is number {
    return typeof p === 'number';
  }

  setPage(p: number): void {
    if (p < 1 || p > this.totalPages || p === this.page) { return; }
    this.pageChange.emit(p);
  }

  private range(start: number, end: number): number[] {
    if (end < start) { return []; }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }
}
